/**
 * Tema PUJA para Ant Design 5
 * ============================
 * Tokens semánticos mapeados a la paleta oficial de marca:
 *   --puja-black     #000000
 *   --puja-graphite  #232323
 *   --puja-gold      #b4986d
 *   --puja-pearl     #f3f3f3
 *   --puja-white     #ffffff
 *
 * Tipografía:
 *   Mont       → SOLO wordmark "PUJA" (en componentes específicos)
 *   Montserrat → todo el resto (UI, body, headings, buttons)
 */

const palette = {
  black:    '#000000',
  graphite: '#232323',
  gold:     '#b4986d',
  goldSoft: '#d9c5a3',
  pearl:    '#f3f3f3',
  white:    '#ffffff',
  // Estados (mantenidos cercanos al estándar para UX consistente)
  success:  '#52c41a',
  warning:  '#faad14',
  error:    '#a14848',  // bordo terroso, no rojo genérico, alinea con mood rural-premium
  info:     '#b4986d',  // info usa el mismo gold (no azul tech)
};

export const pujaTheme = {
  token: {
    // ── Colores semánticos ──────────────────────────────────────
    colorPrimary: palette.gold,
    colorSuccess: palette.success,
    colorWarning: palette.warning,
    colorError: palette.error,
    colorInfo: palette.info,

    colorTextBase: palette.graphite,
    colorBgBase: palette.white,
    colorBgLayout: palette.pearl,

    colorLink: palette.gold,
    colorLinkHover: palette.goldSoft,

    // ── Bordes y radios ─────────────────────────────────────────
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    borderRadiusXS: 4,

    // ── Tipografía ──────────────────────────────────────────────
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontWeightStrong: 700,
    lineHeight: 1.55,

    // ── Sombras (más suaves, mood premium) ──────────────────────
    boxShadow: '0 4px 12px rgba(35, 35, 35, 0.08)',
    boxShadowSecondary: '0 2px 6px rgba(35, 35, 35, 0.05)',
    boxShadowTertiary: '0 1px 3px rgba(35, 35, 35, 0.04)',
  },

  components: {
    // ── Botones ─────────────────────────────────────────────────
    Button: {
      controlHeight: 38,
      controlHeightLG: 44,
      controlHeightSM: 32,
      fontWeight: 600,
      primaryShadow: 'none',
      defaultBorderColor: palette.graphite,
    },

    // ── Layout (Sider, Header, Content) ─────────────────────────
    Layout: {
      siderBg: palette.graphite,       // sidebar dark mode
      headerBg: palette.white,         // header blanco
      headerColor: palette.graphite,
      headerHeight: 74,                // alineado con altura del .logo en sidebar
      bodyBg: palette.pearl,           // background pearl
      triggerBg: palette.black,
      triggerColor: palette.gold,
    },

    // ── Menú (sidebar dark mode) ────────────────────────────────
    Menu: {
      darkItemBg: palette.graphite,
      darkItemColor: palette.pearl,
      darkItemSelectedBg: palette.gold,
      darkItemSelectedColor: palette.white,
      darkItemHoverBg: 'rgba(180, 152, 109, 0.18)',
      darkItemHoverColor: palette.goldSoft,
      darkSubMenuItemBg: palette.black,
    },

    // ── Cards ───────────────────────────────────────────────────
    Card: {
      boxShadow: '0 2px 8px rgba(35, 35, 35, 0.06)',
      headerFontSize: 16,
      headerFontSizeSM: 14,
      paddingLG: 24,
    },

    // ── Inputs y forms ──────────────────────────────────────────
    Input: {
      activeBorderColor: palette.gold,
      hoverBorderColor: palette.goldSoft,
      activeShadow: '0 0 0 2px rgba(180, 152, 109, 0.15)',
    },
    Select: {
      optionSelectedBg: 'rgba(180, 152, 109, 0.12)',
      optionSelectedColor: palette.graphite,
    },

    // ── Tabla ───────────────────────────────────────────────────
    Table: {
      headerBg: palette.pearl,
      headerColor: palette.graphite,
      rowHoverBg: 'rgba(180, 152, 109, 0.06)',
      rowSelectedBg: 'rgba(180, 152, 109, 0.10)',
      rowSelectedHoverBg: 'rgba(180, 152, 109, 0.14)',
    },

    // ── Tabs ────────────────────────────────────────────────────
    Tabs: {
      inkBarColor: palette.gold,
      itemSelectedColor: palette.gold,
      itemHoverColor: palette.goldSoft,
    },

    // ── Tag / Badge ─────────────────────────────────────────────
    Tag: {
      defaultBg: palette.pearl,
      defaultColor: palette.graphite,
    },

    // ── Switch / Radio / Checkbox ───────────────────────────────
    Switch: {
      colorPrimary: palette.gold,
      colorPrimaryHover: palette.goldSoft,
    },

    // ── Notification / Message ──────────────────────────────────
    Notification: {
      width: 384,
    },
  },
};

// Exportar palette por separado por si se necesita en CSS-in-JS específicos
export { palette as pujaPalette };
